import type {
  TemplateSummary,
  TemplateDetail,
  TemplateVersion,
  SaveTemplatePayload,
} from "../types/index.js";

const TOKEN_KEY = "label_designer_token";

export function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? "";
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`/api${path}`, { ...options, headers });

  if (!res.ok) {
    let errorMsg = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) errorMsg = body.error;
    } catch {
      // ignore parse errors
    }
    throw new ApiError(res.status, errorMsg);
  }

  return res.json() as Promise<T>;
}

export async function fetchTemplates(): Promise<TemplateSummary[]> {
  const data = await request<{ templates: TemplateSummary[] }>("/templates");
  return data.templates;
}

export async function fetchTemplate(
  id: string,
  versionId?: string,
): Promise<TemplateDetail> {
  const query = versionId ? `?versionId=${encodeURIComponent(versionId)}` : "";
  return request<TemplateDetail>(
    `/templates/${encodeURIComponent(id)}${query}`,
  );
}

export async function fetchVersions(id: string): Promise<TemplateVersion[]> {
  const data = await request<{ versions: TemplateVersion[] }>(
    `/templates/${encodeURIComponent(id)}/versions`,
  );
  return data.versions;
}

export async function saveTemplate(
  payload: SaveTemplatePayload,
): Promise<{ templateId: string; versionId: string }> {
  return request<{ templateId: string; versionId: string }>("/templates/save", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Rename (the ID stays), change the description, archive or unarchive a template. */
export async function updateTemplateMeta(
  id: string,
  patch: { label?: string; description?: string; archived?: boolean; bleedMm?: number | null },
): Promise<void> {
  await request(`/templates/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

/** New label size as a new version; returns elements that now lie outside the label. */
export async function resizeTemplate(
  id: string,
  body: { widthMm: number; heightMm: number; mode: "keep" | "scale"; createdBy: string; comment?: string },
): Promise<{ versionId: string; outside: string[] }> {
  return request<{ versionId: string; outside: string[] }>(`/templates/${encodeURIComponent(id)}/resize`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function restoreVersion(
  id: string,
  versionId: string,
): Promise<void> {
  await request(`/templates/${encodeURIComponent(id)}/restore`, {
    method: "POST",
    body: JSON.stringify({ versionId }),
  });
}

export interface GenerateLabelResult {
  success: boolean;
  filePath: string;
  fileName: string;
  downloadUrl: string;
}

export async function generateLabel(payload: {
  templateId: string;
  versionId?: string;
  data: Record<string, string | string[][]>[];
  options?: {
    copies?: number;
    fileNamePrefix?: string;
    compressImages?: boolean;
    imageMaxDimensionPx?: number;
    imageJpegQuality?: number;
  };
}): Promise<GenerateLabelResult> {
  return request<GenerateLabelResult>("/labels/generate", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Downloads the PDF binary. downloadUrl is the full path e.g. /api/labels/download/... */
export async function downloadLabelBlob(downloadUrl: string): Promise<Blob> {
  const token = getToken();
  const res = await fetch(downloadUrl, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, msg);
  }
  return res.blob();
}

export { ApiError };
